import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { CreditCard, Calendar, Infinity as InfinityIcon, AlertTriangle, CheckCircle2, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABELS = {
  monthly: 'Monthly Subscription',
  yearly: 'Yearly Plan (12-month access)',
  founders: 'Founders — Lifetime Access',
  legacy: 'Complimentary Access',
  admin: 'Admin',
};

export default function AccountSettings() {
  const [billing, setBilling] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletedDone, setDeletedDone] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`${API}/api/account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error();
      localStorage.removeItem('token');
      setShowDeleteConfirm(false);
      setDeletedDone(true);
      setTimeout(() => { window.location.href = '/'; }, 4000);
    } catch {
      setDeleteError("Couldn't delete your account — please try again or email info@gritclicks.com.");
    } finally {
      setDeleting(false);
    }
  };

  const load = () => {
    fetch(`${API}/api/billing/status`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => r.json())
      .then(setBilling)
      .catch(() => setBilling(false));
  };

  useEffect(load, []);

  const handleCancel = async () => {
    if (!window.confirm('Cancel your monthly subscription? You keep full access until the end of your current billing period — no further charges after that.')) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API}/api/billing/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error();
      load();
    } catch {
      alert('Could not cancel — please try again or contact support.');
    } finally {
      setCancelling(false);
    }
  };

  if (deletedDone) {
    return (
      <div className="min-h-screen bg-deep-navy flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="account-deleted-message">
          <CheckCircle2 size={48} className="text-lime mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Your account and data have been deleted.</h1>
          <p className="text-light-gray text-sm">Your ad accounts remain yours. Redirecting to the home page…</p>
        </div>
      </div>
    );
  }

  if (billing === null) {
    return <Layout variant="light"><div className="text-light-gray py-12 text-center">Loading account...</div></Layout>;
  }
  if (billing === false) {
    return <Layout variant="light"><p className="text-light-gray">Could not load billing info.</p></Layout>;
  }

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—');
  const plan = billing.plan_type;
  const active = billing.access?.active;

  return (
    <Layout variant="light">
      <div className="max-w-3xl" data-testid="account-settings-page">
        <h1 className="text-4xl font-bold text-white mb-8">Account & Billing</h1>

        <div className="card mb-6" data-testid="billing-plan-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <CreditCard size={22} className="text-lime" /> Your Plan
            </h2>
            <span
              data-testid="billing-status-badge"
              className={`px-3 py-1 rounded-full text-xs font-bold border ${
                active ? 'bg-lime/15 text-lime border-lime/40' : 'bg-orange/15 text-orange border-orange/40'
              }`}
            >
              {active ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>

          {!plan || plan === null ? (
            <div className="text-center py-6">
              <p className="text-light-gray mb-4">You don't have a plan yet.</p>
              <Link to="/checkout?plan=monthly" className="btn-primary inline-block" data-testid="billing-choose-plan-btn">Choose a Plan →</Link>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-divider pb-3">
                <span className="text-light-gray">Plan</span>
                <span className="text-white font-bold" data-testid="billing-plan-label">{PLAN_LABELS[plan] || plan}</span>
              </div>

              {plan === 'monthly' && (
                <>
                  <div className="flex items-center justify-between border-b border-divider pb-3">
                    <span className="text-light-gray">Subscription status</span>
                    <span className="text-white font-semibold">{billing.subscription_status || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-divider pb-3">
                    <span className="text-light-gray flex items-center gap-2"><Calendar size={15} /> {billing.cancel_at_period_end ? 'Access ends' : 'Next billing date'}</span>
                    <span className="text-white font-semibold" data-testid="billing-next-date">{fmtDate(billing.next_billing_date)}</span>
                  </div>
                  {billing.subscription_status === 'past_due' && (
                    <p className="text-sm text-orange bg-orange/10 border border-orange/30 rounded-lg p-3 flex gap-2">
                      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                      Your last payment failed. Update your card with Stripe — you have a {billing.grace_days}-day grace period before access pauses.
                    </p>
                  )}
                  {billing.cancel_at_period_end ? (
                    <p className="text-sm text-light-gray bg-black rounded-lg p-3" data-testid="billing-cancelled-note">
                      Your subscription is set to cancel — you keep access until {fmtDate(billing.next_billing_date)} and won't be charged again.
                    </p>
                  ) : (
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      data-testid="billing-cancel-btn"
                      className="mt-2 px-5 py-2.5 rounded-lg border border-orange/40 text-orange text-sm font-bold hover:bg-orange/10 disabled:opacity-50"
                    >
                      {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                  )}
                </>
              )}

              {plan === 'yearly' && (
                <>
                  <div className="flex items-center justify-between border-b border-divider pb-3">
                    <span className="text-light-gray flex items-center gap-2"><Calendar size={15} /> Access expires</span>
                    <span className="text-white font-semibold" data-testid="billing-expires-date">{fmtDate(billing.access_expires_at)}</span>
                  </div>
                  <p className="text-sm text-light-gray bg-black rounded-lg p-3">
                    Yearly is a one-time purchase — it does NOT auto-renew. When it expires, just purchase again to continue.
                  </p>
                </>
              )}

              {plan === 'founders' && (
                <p className="text-lime font-bold flex items-center gap-2 text-lg" data-testid="billing-lifetime-label">
                  <InfinityIcon size={20} /> Lifetime access — no expiration, no renewals, ever.
                </p>
              )}

              {plan === 'legacy' && (
                <p className="text-sm text-light-gray">Your account has complimentary access.</p>
              )}

              <div className="flex items-center justify-between pt-2">
                <span className="text-light-gray">Trades unlocked</span>
                <span className="text-white font-semibold" data-testid="billing-trades">{(billing.trades || []).join(', ') || '—'}</span>
              </div>
            </div>
          )}
        </div>

        {plan !== 'founders' && (
          <div className="card bg-black border-orange/40">
            <h3 className="text-white font-bold text-xl mb-2 flex items-center gap-2"><CheckCircle2 size={18} className="text-orange" /> Want to never pay again?</h3>
            <p className="text-light-gray text-sm mb-4">Founders spots include lifetime access to everything — all future campaigns and features included.</p>
            <Link to="/checkout?plan=lifetime" className="text-orange font-bold hover:underline" data-testid="billing-founders-upsell">See Founders Plan →</Link>
          </div>
        )}

        <div className="card mt-6 border-red-500/30" data-testid="danger-zone-card">
          <h3 className="text-white font-bold text-xl mb-2 flex items-center gap-2">
            <Trash2 size={18} className="text-red-400" /> Delete my account and data
          </h3>
          <p className="text-light-gray text-sm mb-4">
            Permanently removes your GritClicks profile and everything we store about you.
            Learn more on our <Link to="/data-deletion" className="text-lime hover:underline">data deletion page</Link>.
          </p>
          <button
            onClick={() => { setDeleteError(''); setShowDeleteConfirm(true); }}
            data-testid="delete-account-btn"
            className="px-5 py-2.5 rounded-lg border border-red-500/50 text-red-400 text-sm font-bold hover:bg-red-500/10 transition-colors"
          >
            Delete my account and data
          </button>
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" role="dialog" aria-modal="true" data-testid="delete-confirm-dialog">
            <div className="w-full max-w-md rounded-xl border border-divider bg-card-bg p-6">
              <h4 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-400" /> Delete your account?
              </h4>
              <p className="text-sm text-light-gray leading-relaxed mb-5">
                This permanently deletes your GritClicks account and disconnects your ad accounts.
                Your ad accounts stay yours. This can't be undone.
              </p>
              {deleteError && <p className="text-sm text-orange mb-4" data-testid="delete-error">{deleteError}</p>}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  data-testid="delete-cancel-btn"
                  className="px-5 py-2.5 rounded-lg border border-divider text-light-gray text-sm font-bold hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  data-testid="delete-confirm-btn"
                  className="px-5 py-2.5 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
